import { FC, useEffect, useMemo } from 'react';
import CampaignCard from 'pages/Campaigns/components/CampaignCard/CampaignCard.component';
import Leads from './components/Leads';
import { useParams } from 'react-router-dom';
import useQueryParams from 'hooks/useQueryParams';
import { convertLeadApiResponseToLead } from 'utils/lead';
import { useCampaign, useLeads } from './hooks';
import { BlockUI, SkeletonCard } from 'components';
import { PaginationInfo } from 'models/PaginatedData';
import { queryClient } from 'react-query';
import { CampaignType } from 'models/Campaign';

import './Campaign.styles.scss';

const CampaignPage: FC = () => {
  const { id } = useParams<{ id: string }>();
  const { params } = useQueryParams();

  const { data: campaignData, isLoading: isCampaignLoading } = useCampaign(
    id,
    params?.campaignType as CampaignType,
  );
  const {
    data: leadsData,
    isLoading: leadsLoading,
    isError,
    error,
  } = useLeads({ campaignId: id, ...params });

  const isPersonalCampaign = params?.campaignType === CampaignType.Personal;

  const { items, ...rest } = leadsData || {};
  const leads = useMemo(
    () =>
      items &&
      items.map((item) =>
        isPersonalCampaign ? item : convertLeadApiResponseToLead(item),
      ),
    [isPersonalCampaign, items],
  );

  useEffect(() => {
    if (!items) return;
    items.forEach((lead) => {
      const queryLead = queryClient.getQueryData([
        `${isPersonalCampaign ? 'personal-' : ''}lead`,
        { id: lead._id },
      ]);

      if (!queryLead) {
        queryClient.setQueryData(
          [`${isPersonalCampaign ? 'personal' : ''}lead`, { id: lead._id }],
          {
            ...lead,
            campaign: campaignData,
          },
        );
      }
    });
  }, [campaignData, isPersonalCampaign, items]);

  return (
    <div className="campaign-page">
      <BlockUI
        loading={isCampaignLoading}
        loadingElement={<SkeletonCard className="campaign-page__skeleton" />}
      >
        {campaignData && (
          <CampaignCard campaign={campaignData} hasEdit={true} />
        )}
      </BlockUI>

      {!isError ? (
        <Leads
          pagination={rest as PaginationInfo}
          leads={leads}
          loading={leadsLoading}
          campaignId={id}
          personal={params?.campaignType === CampaignType.Personal}
        />
      ) : (
        <span className="error-message">{error.message}</span>
      )}
    </div>
  );
};

export default CampaignPage;
